import React, { useState, useCallback, useEffect } from "react";
import { Modal } from "flowbite-react";
import { useDropzone } from "react-dropzone";
import FilePreview from "./FilePreview";
import { useFileManager } from "../context/FileMangerContextHelper";
import { useToast } from "../context/ToastContextHelper";

const UploadFileModal = ({
  openModal,
  setOpenModal,
  folders,
  view,
  folderTitle,
  setRefreshFiles,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const defaultFolder = folders.find((folder) => folder.name === folderTitle);

  const [selectedFolder, setSelectedFolder] = useState(
    defaultFolder ? defaultFolder.id : "Choose a folder"
  );
  const { uploadFiles } = useFileManager();
  const { setToastMessage } = useToast();
  const onDrop = useCallback((acceptedFiles) => {
    setUploadedFiles(() => [
      ...uploadedFiles,
      ...acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      ),
    ]);
  });

  const removeFile = (fileToRemove) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((file) => file.name !== fileToRemove.name)
    );
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  return (
    <>
      <Modal
        dismissible
        show={openModal === "dismissible"}
        onClose={() => setOpenModal(undefined)}
      >
        <Modal.Header
          theme={{ title: "text-xl font-bold tracking-tight text-gray-900" }}
        >
          Upload File
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col items-center justify-center w-full">
            <select
              disabled={view === "file-manager-view" ? false : true}
              id="folders"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[#c96b1a] focus:border-[#c96b1a] block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 mb-4"
              onChange={(e) => setSelectedFolder(e.target.value)}
              value={selectedFolder}
            >
              <option value={"Choose a folder"}>Choose a folder</option>
              {folders.map((folder) => {
                return (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                );
              })}
            </select>
            <div
              {...getRootProps({
                className: "w-full h-full",
              })}
            >
              <div className="flex flex-col items-center justify-center w-full border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600">
                <div className="flex flex-col items-center justify-center pt-5 pb-6 h-full">
                  <svg
                    className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 20 16"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                    />
                  </svg>
                  <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-semibold">Click to upload</span> or
                    drag and drop
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    PNG, JPG, PDF, XLS, XLSX, TXT, DOC, DOCX
                  </p>
                </div>
                <input {...getInputProps()} />
                <FilePreview files={uploadedFiles} removeFile={removeFile} />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="p-0.5 rounded-md bg-[#003464] text-center text-sm font-semibold text-white shadow-sm hover:bg-[#c96b1a] flex flex-row justify-between items-center hover:shadow-lg hover:ring-2 hover:ring-[#c96b1a]"
            onClick={() => {
              if (selectedFolder !== null && uploadedFiles.length > 0) {
                uploadFiles(uploadedFiles, selectedFolder).finally(() => {
                  if (setRefreshFiles) {
                    setRefreshFiles(true);
                  }
                });
                setUploadedFiles([]);
                setOpenModal(undefined);
              } else {
                if (selectedFolder === null) {
                  return setToastMessage(
                    "You can't upload without selecting folder!"
                  );
                }
                if (uploadedFiles.length === 0) {
                  return setToastMessage("Drop your files first!");
                }
              }
            }}
          >
            <span className="flex items-center transition-all duration-200 rounded-md text-sm px-4 py-2">
              Upload
            </span>
          </button>
          <button
            className="p-0.5 rounded-md bg-white text-center text-sm font-semibold text-black shadow-sm hover:text-[#c96b1a] flex flex-row justify-between items-center hover:shadow-lg hover:ring-2 hover:ring-[#c96b1a]"
            onClick={() => {
              setUploadedFiles([]);
              setOpenModal(undefined);
            }}
          >
            <span className="flex items-center transition-all duration-200 rounded-md text-sm px-4 py-2">
              Cancel
            </span>
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UploadFileModal;
